import { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { createActionBusWithContextsFactory } from './controllers/actions';
import { createContextStore } from './controllers/context';
import { createSurfaceStore } from './controllers/store';
import { buildCombinedKey } from './utils/buildCombinedKey';
import { getModulePathsInSurface } from './utils/getModulePathsInSurface';
import { setModuleStateAtPath } from './utils/setModuleState';
import { loadSurfaceExperience } from './utils/ufo';
const isEagerSurfaceInitialization = surfaceInit => !surfaceInit.load || surfaceInit.load === 'EAGER';
const eagerLoadSurfaces = surfaceLoader => async (surfaces, update, context) => {
  Object.entries(surfaces).forEach(async ([name, surfaceInit]) => {
    if (!isEagerSurfaceInitialization(surfaceInit)) {
      return;
    }
    const {
      surfaceType,
      surfaceKey,
      fallback
    } = surfaceInit;
    const experienceMetaData = {
      metadata: {
        surfaceType,
        surfaceKey
      }
    };
    const surfaceLoaderPromise = loadSurfaceExperience.start();
    const {
      surface,
      error
    } = await surfaceLoader({
      surfaceType,
      surfaceKey,
      contextResolver: () => {
        const {
          switcherMenuHasBeenOpened,
          ...rest
        } = context || {
          switcherMenuHasBeenOpened: true
        };
        return rest;
      }
    });
    await surfaceLoaderPromise;
    if (surface) {
      // only record success metric in eager load as failure will be retried
      await loadSurfaceExperience.success(experienceMetaData);
    }
    update(name, {
      surface,
      isLoading: !surface,
      fallback,
      error
    });
  });
};
const isSurfaceStore = initializationOptions => !!initializationOptions.getCurrentSurfaceState;
const createMutateAtModuleFactory = (getCurrentState, update) => (moduleType, moduleKey) => mutation => {
  const state = getCurrentState();
  update(state.name, ({
    surface
  }) => {
    var _get;
    const state = getCurrentState();
    const modulePaths = state.surface ? getModulePathsInSurface(state.surface) : new Map();
    if (!surface) {
      return {
        surface
      };
    }
    const modulePath = modulePaths.get(buildCombinedKey(moduleType, moduleKey));
    if (!modulePath) {
      throw Error(`tried to update surface module with id of ${moduleKey} path but could not find that module`);
    }
    const currentModuleConfiguration = (_get = get(state.surface, modulePath)) === null || _get === void 0 ? void 0 : _get.fields;
    if (typeof mutation === 'function' && !currentModuleConfiguration) {
      throw Error(`tried to access currentState of surface module with id of ${moduleKey} path but could not find that module`);
    }
    return {
      surface: setModuleStateAtPath(modulePath)(surface, typeof mutation === 'function' ? mutation(currentModuleConfiguration) : mutation)
    };
  });
};
const isContextReady = (contextDependencies = []) => subscriberContext => contextDependencies.every(dep => {
  if (typeof dep === 'string') {
    return Object.keys(subscriberContext).includes(dep);
  }
  if (dep !== null && dep !== void 0 && dep.match) {
    var _subscriberContext$de;
    return !!(subscriberContext !== null && subscriberContext !== void 0 && (_subscriberContext$de = subscriberContext[dep.field]) !== null && _subscriberContext$de !== void 0 && _subscriberContext$de.match(dep.match));
  }
  if (dep !== null && dep !== void 0 && dep.required) {
    var _subscriberContext$de2;
    return !!((_subscriberContext$de2 = subscriberContext === null || subscriberContext === void 0 ? void 0 : subscriberContext[dep.field]) !== null && _subscriberContext$de2 !== void 0 ? _subscriberContext$de2 : false);
  }
  return true;
});
const transformSurfacesToSubscribers = (surfaceLoader, update) => surfaces => Object.entries(surfaces).filter(surface => surface[1].load === 'LAZY' || surface[1].load === undefined).map(([surfaceName, {
  contextDependencies,
  ...surface
}]) => [({
  subscriberContext
}) => {
  if (!isContextReady(contextDependencies)(subscriberContext)) {
    return;
  }
  eagerLoadSurfaces(surfaceLoader)({
    [surfaceName]: surface
  }, update, subscriberContext);
}, contextDependencies === null || contextDependencies === void 0 ? void 0 : contextDependencies.map(dep => typeof dep === 'string' ? dep : dep.field), {
  updateOnSubscribe: true
}]);
export const createSurfaceSdk = ({
  surfaces,
  surfaceLoader,
  sideEffects = [],
  context = {},
  contextMiddleware
}) => {
  const contextStore = createContextStore({
    initialState: context,
    middleware: contextMiddleware
  });
  const store = isSurfaceStore(surfaces) ? surfaces : (() => {
    const _store = createSurfaceStore(surfaces);
    const surfaceToSubs = transformSurfacesToSubscribers(surfaceLoader, _store.update);
    contextStore.subscribe(...surfaceToSubs(surfaces));
    return _store;
  })();
  const useSurface = (surfaceName, analyticsSideEffect) => {
    var _state$surface$type, _state$surface;
    const [state, setState] = useState(store.getCurrentSurfaceState(surfaceName));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const createMutateAtModule = useCallback(createMutateAtModuleFactory(() => store.getCurrentSurfaceState(surfaceName), store.update), []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const createActionBusWithContexts = useMemo(() => createActionBusWithContextsFactory({
      sideEffects: analyticsSideEffect ? [...sideEffects, analyticsSideEffect] : sideEffects
    }), [analyticsSideEffect]);
    const lazyLoadSurface = useCallback(async (surfaceType, surfaceKey) => {
      try {
        store.getCurrentSurfaceStateFromKeys(surfaceType, surfaceKey);
        return;
      } catch {
        const experienceMetaData = {
          metadata: {
            surfaceType,
            surfaceKey
          }
        };
        const surfaceLoaderPromise = loadSurfaceExperience.start();
        const {
          surface,
          error
        } = await surfaceLoader({
          surfaceType,
          surfaceKey,
          contextResolver: () => {
            const context = contextStore.getCurrentContext();
            const {
              switcherMenuHasBeenOpened,
              ...rest
            } = context || {
              switcherMenuHasBeenOpened: true
            };
            return rest;
          }
        });
        await surfaceLoaderPromise;
        if (surface) {
          await loadSurfaceExperience.success(experienceMetaData);
        } else {
          await loadSurfaceExperience.failure(experienceMetaData);
        }
        store.update(surfaceName, {
          error,
          surface,
          isLoading: false
        });
      }
    }, [surfaceName]);
    useEffect(() => {
      const unsubscribe = store.subscribe(setState, [surfaceName], {
        updateOnSubscribe: true
      });
      const surfaceInit = store.getSurfaceInitializationOptions(surfaceName);
      const currentState = store.getCurrentSurfaceState(surfaceName);
      if ((surfaceInit === null || surfaceInit === void 0 ? void 0 : surfaceInit.load) === 'LAZY' && surfaceInit !== null && surfaceInit !== void 0 && surfaceInit.surfaceType && surfaceInit !== null && surfaceInit !== void 0 && surfaceInit.surfaceKey || surfaceInit && isEagerSurfaceInitialization(surfaceInit) && (currentState === null || currentState === void 0 ? void 0 : currentState.error) instanceof Error // Try to reload if eager load has previously failed
      ) {
        lazyLoadSurface(surfaceInit.surfaceType, surfaceInit.surfaceKey);
      }
      return unsubscribe;
    }, [surfaceName, lazyLoadSurface]);
    return {
      createMutateAtModule,
      createActionBusAtModule: createActionBusWithContexts({
        client: contextStore.getCurrentContext(),
        surfaceName: (_state$surface$type = (_state$surface = state.surface) === null || _state$surface === void 0 ? void 0 : _state$surface.type) !== null && _state$surface$type !== void 0 ? _state$surface$type : surfaceName // Use surface type from server, otherwise use the surface name given.
      }),

      context: contextStore.getCurrentContext(),
      ...state
    };
  };
  return {
    useSurface,
    updateContext: contextStore.update,
    store
  };
};

// Public exports

export { createSurfaceRenderer } from './controllers/surfaceRenderer';
export { createSurfaceLoader } from './services/surface-loader';